import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import { InvoiceFundingStatus } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { PaymentDetailDto } from '@lsq-frontend/remote-services/financing-graphql';
import { StatusColor } from '@lsq-frontend/common-ui';
import { ClaimService, ResponsiveService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { first } from 'rxjs/operators';
import {
    InvoiceFundingStatusDisplay,
    InvoiceFundingStatusDotEnum
} from '../../../supply-chain-direct.models';
import { InvoiceFundingStatusModifications } from '../invoice-detail-page.component';

@Component({
    selector: 'invoice-payment-detail-card',
    template: ` <div card-body class="card-contents scf-invoice-detail-card">
        <div class="row" *ngIf="paymentDetailsDataSource != null">
            <div class="summary-table-card-wrapper col-12">
                <card
                    i18n-headerTitle
                    headerTitle="Payment Details"
                    id="invoices-table-card"
                    *ngIf="!(responsiveService.mdBelow | async)"
                >
                    <div card-body class="invoice-table">
                        <lsq-data-table
                            *ngIf="paymentDetailsDataSource"
                            [hasHeader]="true"
                            [dataSource]="paymentDetailsDataSource"
                            [isPaginated]="false"
                            [layoutFixed]="true"
                            [columnVisibilities]="{
                                fee: isBuyerFeeEnabled,
                                feeRate: isBuyerFeeEnabled
                            }"
                        >
                            <lsq-column
                                [column]="{
                                    header: statusI18n,
                                    key: 'status',
                                    width: '20%',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <status-pill
                                        [status]="row.paymentStatus"
                                        [color]="statusColor[row.paymentStatus] || processingColor"
                                        [displayValueMap]="invoiceFundingStatusDisplay"
                                    >
                                    </status-pill>
                                </ng-template>
                            </lsq-column>
                            <lsq-date-column
                                [isShortDate]="true"
                                [alignRight]="true"
                                [column]="{
                                    header: paymentDateI18n,
                                    key: 'paymentDate',
                                    width: '20%',
                                    sortable: false
                                }"
                            >
                            </lsq-date-column>
                            <lsq-currency-column
                                [column]="{
                                    header: feeI18n,
                                    key: 'fee',
                                    width: '20%',
                                    sortable: false
                                }"
                            >
                            </lsq-currency-column>
                            <lsq-column
                                [alignRight]="true"
                                [column]="{
                                    header: feeRateI18n,
                                    key: 'feeRate',
                                    width: '20%',
                                    sortable: false
                                }"
                            >
                                <ng-template [custom-cell] let-row="row">
                                    <ng-container *ngIf="row.paymentStatus == 'EARLY_PAID'">
                                        <div>{{ row.invoicePayment || 0 | lsqPercent }}</div>
                                    </ng-container>
                                    <ng-container *ngIf="row.paymentStatus != 'EARLY_PAID'">
                                        <div>----</div>
                                    </ng-container>
                                </ng-template>
                            </lsq-column>
                            <lsq-currency-column
                                [column]="{
                                    header: invoicePaymentI18n,
                                    key: 'invoicePayment',
                                    width: '20%',
                                    sortable: false
                                }"
                            >
                            </lsq-currency-column>
                        </lsq-data-table>
                    </div>
                </card>
                <div *ngIf="responsiveService.mdBelow | async">
                    <h2 class="mobile-section-header"></h2>
                    <lsq-data-table-mobile
                        [dataSource]="paymentDetailsDataSource"
                        [isPaginated]="false"
                    >
                        <ng-template #mobileCardHeader let-row="row">
                            <div class="mtc-header-content">
                                <div class="invoice-list-mobile-card-header">
                                    <div class="mtc-header-content-left">
                                        <div class="mtc-header-title">
                                            {{ row.invoicePayment | currencyDash }}
                                        </div>
                                    </div>
                                    <div class="mtc-header-content-right">
                                        <status-pill
                                            [status]="row.paymentStatus"
                                            [color]="
                                                statusColor[row.paymentStatus] || processingColor
                                            "
                                            [displayValueMap]="invoiceFundingStatusDisplay"
                                        >
                                        </status-pill>
                                    </div>
                                </div>
                            </div>
                        </ng-template>
                        <ng-template #mobileCardColumns let-row="row">
                            <div class="card-item" *ngIf="row.isBuyerPaidFeesEnabled">
                                <div class="card-item-label">
                                    {{ feeRateI18n }}
                                </div>
                                <div class="card-item-value">
                                    {{
                                        row.paymentStatus == 'EARLY_PAID'
                                            ? (row.feeRate || 0 | lsqPercent)
                                            : row.feeRate
                                    }}
                                </div>
                            </div>
                            <div class="card-item" *ngIf="row.isBuyerPaidFeesEnabled">
                                <div class="card-item-label">
                                    {{ feeI18n }}
                                </div>
                                <div class="card-item-value">
                                    {{ row.fee | lsqCurrency }}
                                </div>
                            </div>
                            <div class="card-item">
                                <div class="card-item-label">
                                    {{ paymentDateI18n }}
                                </div>
                                <div class="card-item-value">
                                    {{ row.paymentDate | lsqDate: 'shortDate' }}
                                </div>
                            </div>
                        </ng-template>
                    </lsq-data-table-mobile>
                </div>
            </div>
        </div>
    </div>`,
    styleUrls: ['./invoice-payment-detail-card.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BuyerAccountInvoicePaymentDetailCardComponent implements OnInit {
    @Input() public paymentDetailsDataSource!: LsqListSource<PaymentDetailDto>;
    public readonly statusColor = StatusColor;
    public readonly invoiceFundingStatus = InvoiceFundingStatus;
    public readonly invoiceFundingStatusDotEnum = InvoiceFundingStatusDotEnum;
    public readonly invoiceFundingStatusModifications = InvoiceFundingStatusModifications;

    public readonly statusI18n = this._i18n('Status');
    public readonly paymentDateI18n = this._i18n('Payment Date');
    public readonly feeRateI18n = this._i18n('Fee Rate');
    public readonly feeI18n = this._i18n('Fee');
    public readonly invoicePaymentI18n = this._i18n({ value: 'Invoice Payment' });
    public isBuyerFeeEnabled = false;
    public isFinancingAccountPortalUser = false;

    constructor(
        private _i18n: I18n,
        public invoiceFundingStatusDisplay: InvoiceFundingStatusDisplay,
        private _claimService: ClaimService,
        public responsiveService: ResponsiveService
    ) {
        this.isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
    }
    async ngOnInit(): Promise<void> {
        const rows = await this.paymentDetailsDataSource.rows$.pipe(first()).toPromise();
        this.isBuyerFeeEnabled = rows[0].buyerPaidFeesEnabled!!;
    }
}
